import React, { useEffect, useState, useMemo, createRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Select from 'components/form/Select';
import Switch from 'components/form/Switch';
import Popconfirm from 'components/Popconfirm';
import Button from 'components/Button';
import { getUsers } from 'services/users';
import {
  getNotificationSettings,
  saveNotificationSettings,
  deleteNotificationSettings,
  getConnectCards,
} from 'services/connect-card.service';
import { useSelector } from 'react-redux';
import { snack } from 'utils/toast';
import SMSComposer from 'components/composers/SMSComposer';
import EmailComposer from 'components/composers/EmailComposer';
import useRequest from 'hooks/useRequest';
import useConnectionOption from 'hooks/useConnectionOptions';
import { htmlToText } from 'components/form/AdvanceTextEditor';

const Recipient = ({
  values,
  connections,
  contacts,
  defaultExpanded,
  onSave,
  onCancel,
  onDelete,
}) => {
  const [t] = useTranslation('connectCard');
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [data, setData] = useState(values);

  const toggleExpanded = () => setExpanded(!expanded);

  const onTextChange = (value) => {
    setData({ ...data, notification_template_content: value });
  };

  const onConnectionChange = (connection_id) => {
    setData({ ...data, connection_id });
  };

  const onContactSelect = (val) => {
    setData({ ...data, contact_id: val });
  };

  const onQuickReplySelect = (item) => {
    const { template_content } = item;
    onTextChange(template_content);
  };

  const handleSave = () => {
    const payload = { ...data };
    if (type === 'sms') {
      payload.notification_template_content = htmlToText(
        payload.notification_template_content,
      );
    }
    return onSave(payload);
  };

  const type = values.notification_template_type;
  const connRequired = type === 'sms';
  const contact = contacts.find((c) => c.value === data.contact_id);

  return (
    <Box mb={1}>
      <Accordion expanded={expanded} variant="outlined">
        <AccordionSummary
          onClick={toggleExpanded}
          expandIcon={<ExpandMoreIcon />}
          sx={{ fontWeight: 500 }}
        >
          {contact
            ? `${type === 'sms' ? 'Text' : 'Email'} to ${contact.label}`
            : 'Select recipient'}
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <Select
                transformValue
                margin="none"
                value={data.contact_id}
                onChange={onContactSelect}
                options={contacts}
              />
              {type === 'sms' && (
                <>
                  <Typography>{t('common:from', 'From')}</Typography>
                  <Select
                    transformValue
                    margin="none"
                    sx={{ width: 190 }}
                    value={data.connection_id}
                    onChange={onConnectionChange}
                    placeholder="Select"
                    options={connections}
                    formatOptionLabel={(option, { context }) =>
                      context === 'value' ? option.address : option.label
                    }
                  />
                </>
              )}
            </Stack>
            {type === 'email' && (
              <EmailComposer
                id={`staff-notification-email-${data.hash}`}
                message={data.notification_template_content}
                onChange={onTextChange}
                onQuickReplySelect={onQuickReplySelect}
              />
            )}
            {type === 'sms' && (
              <SMSComposer
                id={`staff-notification-sms-${data.hash}`}
                value={data.notification_template_content}
                onChange={onTextChange}
              />
            )}
          </Box>
          <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
            {data.hash && (
              <Popconfirm
                title="Are you sure?"
                onConfirm={() => onDelete(data)}
              >
                <Button variant="text" color="error">
                  Delete
                </Button>
              </Popconfirm>
            )}
            {onCancel && (
              <Button variant="text" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button
              disabled={
                !data.contact_id ||
                !data.notification_template_content ||
                (connRequired && !data.connection_id)
              }
              onClick={handleSave}
            >
              Save
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const StaffNotificationSettings = ({ group, card, onUpdateGroup }) => {
  const [t] = useTranslation('connectCard');

  const [templates, { loading, refetch }] = useRequest(
    getNotificationSettings,
    {
      module_type: 'connect_card',
      module_primary_id: card.id,
    },
  );
  const [contacts, setContacts] = useState([]);
  const account = useSelector((state) => state.settings.account);
  const user = useSelector((state) => state.user.user);

  const [enabled, setEnabled] = useState(
    group.group_form_completion_reminder_enabled === '1',
  );
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadContacts().then(setContacts);
  }, []);

  const toggleExpanded = () => setExpanded(!expanded);

  const onToggle = (e) => {
    const { checked } = e.target;
    setEnabled(checked);
    return onUpdateGroup({
      group_form_completion_reminder_enabled: checked ? '1' : '0',
    })
      .then(() => {
        if (checked) {
          setExpanded(checked);
        }
      })
      .catch((e) => {
        setEnabled(!checked);
        snack.error(e.message);
      });
  };

  const loadContacts = () => {
    return getUsers({ account_id: account.account_id, user_enabled: '1' }).then(
      (resp) =>
        resp.map((c) => ({
          label: `${c.user_first_name} ${c.user_last_name}`,
          value: c.contact_id,
        })),
    );
  };

  const connectionOptions = useConnectionOption();

  const onSaveTemplate = async (values) => {
    try {
      await saveNotificationSettings({
        module_type: 'connect_card',
        module_primary_id: card.id,
        account_id: account.account_id,
        ...values,
      });
      snack.success('Notification message saved');
      setAdding(false);
      return refetch();
    } catch (e) {
      snack.error(e.message);
    }
  };

  const handleDelete = async (tmpl) => {
    try {
      await deleteNotificationSettings(tmpl.hash);
      snack.success('Notification message deleted');
      return refetch();
    } catch (e) {
      snack.error(e.message);
    }
  };

  return (
    <Box mb={3}>
      <Accordion expanded={expanded} elevation={6}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row">
            <Switch margin="none" checked={enabled} onChange={onToggle} />
            <Box onClick={toggleExpanded}>
              <Typography gutterBottom>
                {t(
                  'send-staff-notifications-heading',
                  'Send Staff Notifications',
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'send-staff-notification-desc',
                  'Send a text message or email to notify a staff member when someone completes this connect card.',
                )}
              </Typography>
            </Box>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {templates?.map((i) => (
            <Recipient
              values={i}
              key={i.hash}
              contacts={contacts}
              connections={connectionOptions}
              onSave={onSaveTemplate}
              onDelete={handleDelete}
            />
          ))}
          {adding && adding !== true && (
            <Recipient
              defaultExpanded
              values={{
                notification_template_type: adding,
                notification_template_content: '',
              }}
              contacts={contacts}
              connections={connectionOptions}
              onSave={onSaveTemplate}
              onCancel={() => setAdding(false)}
            />
          )}
          {adding === true && (
            <Stack direction="row" justifyContent="center" spacing={2} mt={2}>
              <Button
                color="cta"
                size="large"
                onClick={() => setAdding('email')}
              >
                Send Email Notification
              </Button>
              <Button color="cta" size="large" onClick={() => setAdding('sms')}>
                Send SMS Notification
              </Button>
            </Stack>
          )}
          <Box mt={2}>
            <Box mt={1} mb={2}>
              {!adding && (
                <Button startIcon={<AddIcon />} onClick={() => setAdding(true)}>
                  {!templates?.length
                    ? 'Add Recipient'
                    : 'Add Another Recipient'}
                </Button>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default StaffNotificationSettings;
